import debounce from 'lodash/debounce';
import { Directive, ElementRef, OnInit, OnDestroy } from '@angular/core';

const rAF = requestAnimationFrame;
const SCROLL = {
  bottom: elm =>
    // rAF is used to remove forced browser reflow
    rAF(() => {
      const bottom = elm.scrollHeight;
      rAF(() => elm.scrollTop = bottom);
    }),
};
const IS_ATTACHED = {
  bottom: elm => elm.scrollTop + elm.clientHeight + 10 >= elm.scrollHeight,
  top: elm => elm.scrollTop <= 10,
};

@Directive({
  selector: '[scrollGlue]',
  exportAs: 'scrollGlue',
})
export class ScrollGlueDirective implements OnInit, OnDestroy {
  public el;
  public glueElm;
  public mutationObserver;
  public listToObserve;

  constructor(el: ElementRef) {
    this.el = el.nativeElement;
  }

  public ngOnInit() {
    // use .scroll-glue element if found
    // else use element[0]
    // .scroll-glue element is the one whose direct child list
    // we want observe
    this.glueElm = this.el.querySelector('.scroll-glue') || this.el;
    this._initObserveWhenAttached();
    this._initMutationObserver();
    this.startObserving();
  }

  public ngOnDestroy() {
    this.mutationObserver.disconnect();
    this.mutationObserver = null;
    this.listToObserve = null;
  }

  public _initObserveWhenAttached() {
    // scroll handler to check if scroller is attached to bottom/top
    // if it's attached start mutation observer, otherwise stop it.
    // remember calling mutationObserver.observe mutiple times won't
    // start multiple observations.
    this.el.addEventListener('scroll',
      debounce(() => {
        // start/stop observer based on scroll position
        if (IS_ATTACHED['bottom'](this.el)) {
          this.startObserving();
        } else {
          this.stopObserving();
        }
      }, 100)
    , false);
  }

  public _initMutationObserver() {
    // init mutation observer
    this.mutationObserver = new MutationObserver(() =>
      // scroll because childList is updated
      this.scroll(),
    );
  }

  public scroll() {
    const direction = 'bottom';
    SCROLL[direction](this.el);
  }

  public resetGlue() {
    this.scroll();
    this.startObserving();
  }

  public startObserving() {
    const mutationObserver = this.mutationObserver;
    return mutationObserver && mutationObserver.observe(this.glueElm, { childList: true });
  }

  public stopObserving() {
    const mutationObserver = this.mutationObserver;
    return mutationObserver && mutationObserver.disconnect();
  }
}
